import { C, assemble, blocks, saveBlob, fetchLogo, when, day, type CellSpec as KitCell, type Shade } from "../../reportDocx";
import { OBJECTIVE_METRICS, VERDICT, objectiveValue, type Objective } from "./Objectives";
import type { Campaign, CampaignRun } from "../../api/client";
import { evidenceNotes, noObservedOutages, clock, compareRuns, delta, duration, markerTimings, ms, pct, seriesChartSVG, type Marker, type RunReport } from "./report";
import { APPS, flowHeadline } from "../traffic";
import { METRICS, WORD, configDiff, configTexts, flows, inPlace, judge, label, summarize, toneOf, type BehaviorResult, type DatasetComparison, type Tone } from "./runEvidence";

/**
 * A realm's runs as a Word document: built in the browser with the docx
 * library (loaded only when somebody exports, so the console does not carry it
 * on every page), with the same charts the report page draws, rasterised.
 *
 * Laid out to be handed on: a title page, a table of contents Word fills in,
 * an executive summary, then each run on its own pages in the order they ran
 * (overview, the steps a playbook took, test behaviour and device state
 * starting versus final, measurements, outages, charts, events), and a short
 * glossary. Headings, tables and paragraphs are Word's own, not a picture of
 * a page, so it is edited like any other document.
 */

/** One step of a playbook run, as the run recorded it. */
export interface ExportStep {
  id: string; name: string; kind: string; state: string;
  started_at?: string; ended_at?: string; error?: string;
  device_id?: string; device_ids?: string[]; sync_all?: boolean; interface?: string; ping_target?: string; seconds?: number;
  template_id?: string; change?: { admin_up?: boolean; description?: string }; restore?: boolean; hold_seconds?: number; handle?: string; instructions?: string;
}

/** What a playbook run did and found, beyond its measurements. */
export interface AutomationEvidence {
  state: string; error?: string; error_detail?: string; started_at: string; ended_at?: string;
  steps: ExportStep[]; comparison: DatasetComparison[]; behavior: BehaviorResult[];
}

export interface ExportRun {
  caseName: string;
  /** Which run of its playbook this is, counting from 1. */
  number: number;
  run: CampaignRun;
  report: RunReport | null;
  markers?: Marker[];
  /** The run this one is compared with, if it has a baseline. */
  baseline?: { label: string; report: RunReport } | null;
  /** The playbook automation's own evidence, for runs that had steps. */
  automation?: AutomationEvidence | null;
}

export interface ExportOptions {
  /** A device's name for its id. */
  deviceName?: (id: string) => string;
  /** A template's name for its id. */
  templateName?: (id: string) => string;
  /** The logo for the title page. */
  logo?: { data: Uint8Array; type: "jpg" | "png"; width: number; height: number } | null;
}

const OUTCOME: Record<string, string> = { pass: "Pass", fail: "Fail", inconclusive: "Inconclusive", "": "Not recorded" };

const fromTone = (t: Tone): Shade => t === "ok" ? "ok" : t === "changed" ? "warn" : t === "bad" ? "bad" : t === "churn" ? "info" : "none";

/** A run's result in a word, and its colour: the operator's outcome, else what the automation reached. */
function result(e: ExportRun): { word: string; shade: Shade } {
  const o = e.run.outcome;
  if (o) return { word: OUTCOME[o] ?? o, shade: o === "pass" ? "ok" : o === "fail" ? "bad" : "warn" };
  const a = e.automation;
  if (a) {
    if (a.state === "completed") return { word: "Completed", shade: "ok" };
    if (a.state === "failed") return { word: "Failed", shade: "bad" };
    if (a.state === "cancelled") return { word: "Stopped", shade: "none" };
    return { word: "In progress", shade: "info" };
  }
  return { word: e.run.stopped_at ? "Not recorded" : "In progress", shade: "none" };
}

/** Rasterise a standalone SVG to PNG bytes, at twice its size for print. */
async function svgToPNG(svg: string, width: number, height: number): Promise<Uint8Array> {
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("could not draw a chart"));
      img.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = width * 2;
    canvas.height = height * 2;
    const g = canvas.getContext("2d");
    if (!g) throw new Error("this browser cannot draw charts for export");
    g.scale(2, 2);
    g.drawImage(img, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error("could not encode a chart");
    return new Uint8Array(await blob.arrayBuffer());
  } finally {
    URL.revokeObjectURL(url);
  }
}

const seconds = (a?: string, b?: string) => a && b ? Math.max(0, (Date.parse(b) - Date.parse(a)) / 1000) : undefined;

/** Build the document and hand it to the browser as a download. */
export async function exportCampaignDocx(campaign: Campaign, agentNames: string[], runs: ExportRun[], fileName: string, options: ExportOptions = {}): Promise<void> {
  const logo = options.logo === undefined ? await fetchLogo() : options.logo;
  saveBlob(await buildCampaignDocx(campaign, agentNames, runs, svgToPNG, { ...options, logo }), fileName);
}

/**
 * The document itself. renderPNG turns a chart's SVG into PNG bytes; the
 * browser's canvas by default, replaceable where there is none (tests).
 */
export async function buildCampaignDocx(
  campaign: Campaign, agentNames: string[], given: ExportRun[],
  renderPNG: (svg: string, width: number, height: number) => Promise<Uint8Array> = svgToPNG,
  options: ExportOptions = {},
): Promise<Blob> {
  const d = await import("docx");
  const { Packer, Paragraph, HeadingLevel, Table, ImageRun, AlignmentType, TableOfContents } = d;
  type Block = InstanceType<typeof Paragraph> | InstanceType<typeof Table> | InstanceType<typeof TableOfContents>;
  const deviceName = options.deviceName ?? ((id: string) => id.slice(0, 8));
  const templateName = options.templateName ?? ((id: string) => id.slice(0, 8));
  // In the order they ran, numbered across the realm.
  const runs = [...given].sort((a, b) => a.run.started_at.localeCompare(b.run.started_at));

  const { text, para, muted, heading, pill, divider, table, pairs, banner, spacer } = blocks(d);
  type CellSpec = KitCell;

  // ---- Title page facts --------------------------------------------------
  const first = runs[0]?.run.started_at, last = runs.length ? runs[runs.length - 1]!.run.stopped_at ?? runs[runs.length - 1]!.run.started_at : undefined;
  const tally = new Map<string, { n: number; shade: Shade }>();
  for (const e of runs) { const r = result(e); const t = tally.get(r.word) ?? { n: 0, shade: r.shade }; t.n++; tally.set(r.word, t); }
  const devices = [...new Set(runs.flatMap((e) => [...(e.automation?.comparison ?? []).map((c) => c.device_id), ...(e.automation?.steps ?? []).flatMap((s) => s.device_id && s.device_id !== "00000000-0000-0000-0000-000000000000" ? [s.device_id] : [])]))];
  const playbooks = [...new Set(runs.map((e) => e.caseName))];
  const facts: [string, CellSpec][] = [
    ["Period", first ? (last && day(last) !== day(first) ? `${day(first)} – ${day(last)}` : day(first)) : "No runs"],
    ["Runs", runs.length ? [...tally].map(([w, t]) => `${t.n} ${w.toLowerCase()}`).join(" · ") : "0"],
    ["Playbooks", playbooks.join(", ") || "—"],
    ["Shards in scope", agentNames.join(", ") || "none"],
    ...(devices.length ? [["Devices", devices.map(deviceName).join(", ")] as [string, string]] : []),
    ["Generated", `${when(new Date().toISOString())} by Heimdall`],
  ];

  // ---- Body ---------------------------------------------------------------
  const body: Block[] = [];
  body.push(heading("Executive summary", HeadingLevel.HEADING_1));
  if (!runs.length) body.push(para("This realm has no runs yet."));
  else {
    body.push(new Paragraph({ spacing: { after: 160 }, children: [text(`${runs.length} run${runs.length === 1 ? "" : "s"} of ${playbooks.length} playbook${playbooks.length === 1 ? "" : "s"}: `), ...[...tally].flatMap(([w, t], i) => [...(i ? [text("  ")] : []), pill(`${t.n} ${w}`, t.shade)])] }));
    for (const [i, e] of runs.entries()) {
      const r = e.report, res = result(e), a = e.automation;
      const facts: string[] = [];
      if (a?.error) facts.push(a.error_detail || a.error.replaceAll("_", " "));
      if (r) facts.push(r.outages.length ? `${r.outages.length} outage${r.outages.length === 1 ? "" : "s"}, longest ${duration(r.outages.reduce((m, o) => Math.max(m, o.seconds), 0))}` : "no outages");
      if (a?.behavior.length) {
        const worse = a.behavior.filter((b) => b.status === "compared" && METRICS.some(([k, , get]) => judge(k, get(b.before), get(b.after)) === "bad")).length;
        facts.push(worse ? `${worse} test${worse === 1 ? "" : "s"} degraded` : "tests showed no degradation");
      }
      if (a?.comparison.length) {
        const changed = new Set(a.comparison.filter((c) => toneOf(c.status, c.dataset) === "changed").map((c) => c.device_id)).size;
        facts.push(changed ? `${changed} device${changed === 1 ? "" : "s"} changed` : "devices unchanged");
      }
      body.push(new Paragraph({ bullet: { level: 0 }, spacing: { after: 80 }, children: [text(`Run ${i + 1} · ${e.caseName}: `, { bold: true }), pill(res.word, res.shade), text(facts.length ? `  ${facts.join("; ")}.` : "")] }));
    }
    body.push(spacer());
    body.push(table(
      ["#", "Playbook", "Started", "Duration", "Result", "Outages", "Longest outage", "Tests", "Devices"],
      runs.map((e, i) => {
        const r = e.report, a = e.automation, res = result(e);
        const longest = r?.outages.reduce((m, o) => Math.max(m, o.seconds), 0) ?? 0;
        const worse = a?.behavior.filter((b) => b.status === "compared" && METRICS.some(([k, , get]) => judge(k, get(b.before), get(b.after)) === "bad")).length ?? 0;
        const changed = a ? new Set(a.comparison.filter((c) => toneOf(c.status, c.dataset) === "changed").map((c) => c.device_id)).size : 0;
        const span = r ? r.duration_seconds : seconds(e.run.started_at, e.run.stopped_at ?? undefined);
        return [
          String(i + 1), `${e.caseName} (#${e.number})`, when(e.run.started_at), span !== undefined ? duration(span) : "in progress",
          { text: res.word, shade: res.shade }, r ? String(r.outages.length) : "—", r && r.outages.length ? duration(longest) : "—",
          a?.behavior.length ? { text: worse ? `${worse} degraded` : "OK", shade: worse ? "bad" : "ok" } : "—",
          a?.comparison.length ? { text: changed ? `${changed} changed` : "Unchanged", shade: changed ? "warn" : "ok" } : "—",
        ];
      }),
    ));
  }

  for (const [i, e] of runs.entries()) {
    const r = e.report, a = e.automation, res = result(e);
    body.push(heading(`${i + 1}. ${e.caseName} — run ${e.number}`, HeadingLevel.HEADING_1, true));
    const span = r ? r.duration_seconds : seconds(e.run.started_at, e.run.stopped_at ?? undefined);
    body.push(banner([pill(res.word, res.shade), text(`   Started ${when(e.run.started_at)}`, { color: C.ink, size: 20 }), text(span !== undefined ? ` · ${duration(span)}` : " · in progress", { color: C.ink, size: 20 })], res.shade));
    body.push(spacer());
    let sections = 0;
    const section = (title: string) => { if (sections++) body.push(divider()); body.push(heading(title, HeadingLevel.HEADING_2)); };

    section("Overview");
    body.push(pairs([
      ["Playbook", `${e.caseName} (run ${e.number} of this playbook)`],
      ["Started", `${when(e.run.started_at)}${e.run.started_by ? ` by ${e.run.started_by}` : ""}`],
      ["Stopped", e.run.stopped_at ? `${when(e.run.stopped_at)}${e.run.stopped_by ? ` by ${e.run.stopped_by}` : ""}` : "in progress"],
      ["Duration", span !== undefined ? duration(span) : "—"],
      ["Outcome", { text: OUTCOME[e.run.outcome] ?? e.run.outcome, shade: e.run.outcome === "pass" ? "ok" : e.run.outcome === "fail" ? "bad" : e.run.outcome ? "warn" : undefined }],
      ...(a ? [["Automation", { text: a.state === "failed" ? `Failed: ${a.error_detail || (a.error ?? "").replaceAll("_", " ")}` : a.state === "completed" ? "Completed every step" : a.state, shade: a.state === "failed" ? "bad" : a.state === "completed" ? "ok" : "none" }] as [string, CellSpec]] : []),
      ...(e.baseline ? [["Baseline", e.baseline.label] as [string, string]] : []),
    ]));
    if (e.run.notes.trim()) {
      body.push(spacer(60));
      body.push(para("Notes", { bold: true, color: C.brand }));
      for (const line of e.run.notes.split("\n")) body.push(para(line));
    }

    if (a?.steps.length) {
      section("Steps");
      body.push(table(["#", "Step", "Action", "Target", "Started", "Took", "Result"], a.steps.map((s, n) => {
        const shade: Shade = s.state === "completed" ? "ok" : s.state === "failed" || s.state === "unknown" ? "bad" : s.state === "cancelled" || s.state === "queued" ? "none" : "info";
        return [String(n + 1), s.name, stepAction(s), stepTarget(s, deviceName, templateName), s.started_at ? clock(s.started_at) : "—",
          seconds(s.started_at, s.ended_at) !== undefined ? duration(seconds(s.started_at, s.ended_at)!) : "—",
          { text: `${s.state.replaceAll("_", " ")}${s.error ? `\n${s.error.replaceAll("_", " ")}` : ""}`, shade }];
      })));
    }

    if (a?.behavior.length) {
      section("Test behaviour, starting versus final");
      body.push(muted("Each test is measured before the steps and again after them. The change is final minus starting; latency within a few milliseconds or 20% is ordinary variation."));
      for (const b of a.behavior) {
        const name = a.steps.find((s) => s.id === b.step_id)?.name ?? "Test";
        const rows = METRICS.map(([key, title, get, unit]) => ({ key, title, unit, before: get(b.before), after: get(b.after) })).filter((m) => m.before !== undefined || m.after !== undefined);
        const tones = rows.map((m) => judge(m.key, m.before, m.after));
        const verdict: [string, Shade] = b.status !== "compared" ? ["Inconclusive", "none"] : tones.includes("bad") ? ["Degraded", "bad"] : tones.includes("changed") ? ["Slower", "warn"] : ["No degradation", "ok"];
        body.push(new Paragraph({ spacing: { before: 120, after: 80 }, children: [text(name, { bold: true, color: C.brand, size: 22 }), text("  "), pill(verdict[0], verdict[1])] }));
        const fmtv = (v: number | undefined, unit: string) => v === undefined ? "—" : unit === "%" ? `${v.toFixed(1)}%` : `${v.toFixed(2)} ms`;
        if (rows.length) body.push(table(["Measure", "Starting", "Final", "Change"], rows.map((m, k) => [m.title, fmtv(m.before, m.unit), fmtv(m.after, m.unit),
          { text: m.before !== undefined && m.after !== undefined ? `${m.after - m.before >= 0 ? "+" : ""}${(m.after - m.before).toFixed(m.unit === "%" ? 1 : 2)}${m.unit === "%" ? " pts" : " ms"}` : "—", shade: fromTone(tones[k]!) }])));
        const fl = [flows(b.before), flows(b.after)];
        if (fl[0] || fl[1]) body.push(para(`Starting: ${fl[0] ?? "unavailable"}. Final: ${fl[1] ?? "unavailable"}.`));
        if (b.path_reason) body.push(muted(`Paths ${b.paths_changed ? "changed" : "unchanged"}: ${b.path_reason}`));
        else if (!rows.length && b.reason) body.push(muted(b.reason));
      }
    }

    if (a?.comparison.length) {
      section("Device state, starting versus final");
      body.push(muted("Each device is collected before the steps and after them. MAC and ARP entries learned or aged out are shown as table activity, not as the device changing; counters, ages and uptime are ignored."));
      const ids = [...new Set(a.comparison.map((c) => c.device_id))];
      const rank = (id: string) => { const t = a.comparison.filter((c) => c.device_id === id).map((c) => toneOf(c.status, c.dataset)); return t.includes("bad") ? 0 : t.includes("changed") ? 1 : t.every((x) => x === "unknown") ? 2 : 3; };
      for (const id of ids.sort((x, y) => rank(x) - rank(y) || deviceName(x).localeCompare(deviceName(y)))) {
        const rows = a.comparison.filter((c) => c.device_id === id);
        const of = (t: Tone) => rows.filter((c) => toneOf(c.status, c.dataset) === t);
        const changed = of("changed"), bad = of("bad"), unknown = of("unknown"), ok = of("ok"), churn = of("churn");
        const tone: Shade = bad.length ? "bad" : changed.length ? "warn" : unknown.length && !ok.length ? "none" : "ok";
        const touched = a.steps.filter((s) => s.device_id === id || s.device_ids?.includes(id)).map((s) => s.name);
        body.push(new Paragraph({ spacing: { before: 160, after: 80 }, children: [text(deviceName(id), { bold: true, color: C.brand, size: 22 }), text("  "), pill(changed.length ? `${changed.length} changed` : tone === "bad" ? "Collection failed" : tone === "none" ? "Inconclusive" : "Unchanged", tone), ...(touched.length ? [text(`   touched by ${touched.join(", ")}`, { italics: true, color: C.muted, size: 18 })] : [])] }));
        const lines: CellSpec[][] = [];
        for (const c of [...changed, ...churn]) {
          const texts = configTexts(c);
          if (texts) {
            // A configuration, line by line: only what changed, with context.
            const out: { text: string; color?: string; italics?: boolean }[] = [];
            for (const h of configDiff(texts[0], texts[1])) {
              if (out.length) out.push({ text: "⋯", color: C.muted });
              if (h.section) out.push({ text: `in ${h.section}`, color: C.brand, italics: true });
              for (const l of h.lines) out.push({ text: `${l.kind === " " ? " " : l.kind === "-" ? "−" : "+"} ${l.text}`, color: l.kind === "+" ? C.ok : l.kind === "-" ? C.bad : C.muted });
            }
            lines.push([{ text: label(c.dataset), shade: "warn" }, out.length ? { text: "", lines: out.slice(0, 400) } : "Only lines IOS rewrites itself changed."]);
            continue;
          }
          const ip = inPlace(c), isChurn = toneOf(c.status, c.dataset) === "churn";
          const what = ip ? ip.slice(0, 25).join("\n") : [...c.added.slice(0, 40).map((x) => `+ ${summarize(x)}`), ...c.removed.slice(0, 40).map((x) => `− ${summarize(x)}`)].join("\n");
          lines.push([{ text: `${label(c.dataset)}${isChurn ? " (activity)" : ""}`, shade: isChurn ? "info" : "warn" }, { text: what || (c.truncated ? "Detail exceeds the export limit." : "—"), mono: true }]);
        }
        for (const c of bad) lines.push([{ text: label(c.dataset), shade: "bad" }, c.reason || "could not be collected"]);
        if (unknown.length) lines.push([{ text: "Inconclusive", shade: "none" }, `${unknown.map((c) => label(c.dataset)).join(", ")} — ${[...new Set(unknown.map((c) => c.reason || "missing or incomparable observations"))].join("; ")}`]);
        if (ok.length) lines.push([{ text: WORD.ok, shade: "ok" }, ok.map((c) => label(c.dataset)).join(", ")]);
        body.push(table(["Dataset", "What changed"], lines));
      }
    }

    if (r) {
      if (r.truncated) body.push(muted(`Note: ${r.truncated}.`));
      section("Measurements");
      if (r.series.length === 0) body.push(muted("No measurements were recorded by the shards in scope during this run."));
      else body.push(table(
        ["Test", "Agent", "Samples", "Largest gap", "Loss", "Avg RTT", "p95 RTT", "Outages", "Longest", "First loss after start"],
        r.series.map((s) => [
          s.test, s.agent, s.summary.samples.toLocaleString(), s.evidence ? duration(s.evidence.largest_gap_seconds) : "Not recorded",
          { text: pct(s.summary.loss_ratio), shade: s.summary.loss_ratio ? (s.summary.loss_ratio > 0.01 ? "bad" : "warn") : "ok" },
          ms(s.summary.avg_ms), ms(s.summary.p95_ms), { text: String(s.summary.outages), shade: s.summary.outages ? "bad" : "ok" },
          s.summary.longest_outage_seconds != null ? duration(s.summary.longest_outage_seconds) : "—",
          s.summary.first_loss_after_seconds != null ? duration(s.summary.first_loss_after_seconds) : "—",
        ]),
      ));

      section("Acceptance result");
      if (r.assessment) {
        body.push(para(`${VERDICT[r.assessment.verdict] ?? r.assessment.verdict} · Criteria version ${r.assessment.version}. Evaluated frozen evidence separately from the operator outcome.`));
        body.push(table(["Rule / scope", "Threshold", "Observed", "Result", "Evidence"], r.assessment.results.map((x) => [
          `${x.name} / ${x.scope}`, `${OBJECTIVE_METRICS[x.metric as Objective["metric"]]?.floor ? "≥" : "≤"} ${objectiveValue(x.metric, x.threshold)}`, objectiveValue(x.metric, x.actual),
          { text: VERDICT[x.verdict] ?? x.verdict, shade: x.verdict === "met" ? "ok" : x.verdict === "missed" ? "bad" : "none" }, x.reason,
        ])));
      } else body.push(muted("No criteria were captured for this run. Performance has not been automatically assessed."));

      if (e.baseline) {
        section(`Compared with ${e.baseline.label}`);
        body.push(table(
          ["Test", "Agent", "Loss", "Δ loss", "Avg RTT", "Δ avg", "p95 RTT", "Δ p95", "Outages"],
          compareRuns(r, e.baseline.report).map((c) => [
            c.test, c.agent,
            `${c.run ? pct(c.run.loss_ratio) : "—"} / ${c.base ? pct(c.base.loss_ratio) : "—"}`,
            delta(c.run?.loss_ratio, c.base?.loss_ratio, "pp"),
            `${ms(c.run?.avg_ms)} / ${ms(c.base?.avg_ms)}`, delta(c.run?.avg_ms, c.base?.avg_ms, "ms"),
            `${ms(c.run?.p95_ms)} / ${ms(c.base?.p95_ms)}`, delta(c.run?.p95_ms, c.base?.p95_ms, "ms"),
            `${c.run?.outages ?? "—"} / ${c.base?.outages ?? "—"}`,
          ]),
        ));
        body.push(muted("This run / the baseline. Δ is this run minus the baseline; loss in percentage points."));
      }

      if (r.traffic) {
        section("Applications");
        body.push(para(`${r.traffic.profile || "A traffic mix"} between ${r.traffic.client} and ${r.traffic.responder}, both ways throughout the run.`));
        if (r.traffic.error) body.push(para(`The mix did not run as asked: ${r.traffic.error}`, { italics: true, color: C.bad }));
        if (r.traffic.result) {
          const dir = (x?: { loss_pct: number; jitter_ms: number; dscp_kept?: boolean; dscp_seen?: number }) =>
            x ? `${x.loss_pct.toFixed(2)}% loss, ${x.jitter_ms.toFixed(1)} ms jitter${x.dscp_kept === false ? `, DSCP ${x.dscp_seen} on arrival` : ""}` : "—";
          body.push(table(
            ["Flow", "Application", "Result", "Client → responder", "Responder → client", "DSCP"],
            r.traffic.result.flows.map((f) => [f.id, `${APPS[f.app]?.label ?? f.app} × ${f.count}`, flowHeadline(f).text, dir(f.up), dir(f.down), String(f.dscp)]),
          ));
        }
      }

      section("Outages");
      if (r.outages.length === 0) body.push(para(noObservedOutages(r), { italics: true, color: C.ok }));
      else body.push(table(
        ["Test", "Agent", "Began", "Restored", "Duration", "Lost samples", "Into the run"],
        r.outages.map((o) => [o.test, o.agent, clock(o.start), { text: o.recovered ? clock(o.end) : "not by stop", shade: o.recovered ? undefined : "bad" }, duration(o.seconds), String(o.lost), duration(o.after_start_seconds)]),
      ));

      const markers = e.markers ?? [];
      if (markers.length) {
        section("Markers");
        body.push(table(
          ["Time", "Marker", "Into the run", "First loss after", "Restored after", "Where"],
          markerTimings(r, markers).map((t) => [
            clock(t.marker.at), t.marker.label, duration(t.intoRun),
            t.lossAfter != null ? duration(t.lossAfter) : "no loss",
            t.restoredAfter != null ? duration(t.restoredAfter) : t.lossAfter != null ? "not by stop" : "—",
            t.where ?? "",
          ]),
        ));
      }

      if (r.series.length) {
        section("Charts");
        for (const s of r.series) {
          const W = 900, H = 260;
          const png = await renderPNG(seriesChartSVG(s, r, { width: W, height: H, markers }), W, H);
          body.push(heading(`${s.test} on ${s.agent}`, HeadingLevel.HEADING_3));
          body.push(new Paragraph({ alignment: AlignmentType.CENTER, children: [new ImageRun({ type: "png", data: png, transformation: { width: 620, height: Math.round((620 * H) / W) } })] }));
          body.push(muted(`Blue: round-trip time. Red bars: lost samples. Shaded: outages.${markers.length ? " Dashed: markers." : ""}`));
        }
      }

      section("Events");
      if (r.events.length === 0) body.push(muted("No events were reported during this run."));
      else body.push(table(["Time", "Agent", "Severity", "Type", "Summary"],
        r.events.map((ev) => [clock(ev.t), ev.agent ?? "", { text: ev.severity, shade: ev.severity === "critical" || ev.severity === "error" ? "bad" : ev.severity === "warning" ? "warn" : undefined }, ev.type, ev.summary])));

      if (r.sessions && r.sessions.length) {
        section("Two-ended tests");
        body.push(table(["Time", "Kind", "From", "To", "State", "Result"],
          r.sessions.map((ss) => [clock(ss.t), ss.kind, ss.client, ss.responder, ss.state, ss.result ?? ""])));
      }

      section("Evidence limitations");
      for (const note of evidenceNotes(r)) body.push(para(note));
    } else if (!a) {
      section("Report");
      body.push(muted("This run is still in progress; its report is made when it stops."));
    }
  }

  body.push(heading("Method and glossary", HeadingLevel.HEADING_1, true));
  for (const [term, meaning] of [
    ["Run", "One execution of a playbook. A playbook with steps starts by collecting every device it touches and measuring its tests, carries out its steps in order, then collects and measures again."],
    ["Starting versus final", "The state and measurements before the steps compared with those after them. A difference is an observation, not an automatic pass or fail."],
    ["Failed pings", "The share of pings (each a short burst of probes) that got no answer at all."],
    ["Loss, packet loss", "The share of individual probes that got no answer. Targets that rate-limit ICMP drop some probes without any ping failing."],
    ["RTT, p95", "Round-trip time, and the time 95% of answered probes came in under."],
    ["Outage", "A stretch of consecutive lost probes, from the first loss to the next answer."],
    ["Table activity", "MAC or ARP entries learned or aged out: hosts coming and going, not configuration."],
    ["Inconclusive", "Evidence was missing, incomplete or could not be compared, so nothing is claimed either way."],
    ["Outcome", "The verdict an operator recorded for the run. Acceptance results are evaluated separately, from frozen evidence, against criteria captured when the run started."],
  ] as [string, string][]) {
    body.push(new Paragraph({ spacing: { after: 100 }, children: [text(`${term}. `, { bold: true, color: C.brand }), text(meaning)] }));
  }

  const doc = assemble(d, { title: campaign.name, kind: "Network validation report", description: campaign.description || "Realm report", logo: options.logo ?? null, facts, body });
  return Packer.toBlob(doc);
}

/** What a step does, in a few words. */
function stepAction(s: ExportStep): string {
  switch (s.kind) {
    case "test": return s.ping_target ? `Ping for ${s.seconds ?? "?"} s` : `Test for ${s.seconds ?? "?"} s`;
    case "traffic": return `Traffic for ${s.seconds ?? "?"} s`;
    case "wait": return `Wait ${s.seconds ?? "?"} s`;
    case "manual": return "Manual checkpoint";
    case "sync": return "Collect device state";
    case "interface": {
      const parts = [s.change?.admin_up === undefined ? "" : s.change.admin_up ? "Enable" : "Shut down", s.change?.description !== undefined ? "set description" : ""].filter(Boolean);
      return `${parts.join(", ") || "Change"} interface${s.restore ? `, restore after ${s.hold_seconds ?? 0} s` : ""}`;
    }
    case "snippet": return "Apply CLI template";
    case "stop": return "Stop background workload";
    default: return s.kind;
  }
}

/** What a step acts on: a device and interface, a ping target, a template. */
function stepTarget(s: ExportStep, deviceName: (id: string) => string, templateName: (id: string) => string): string {
  const device = s.device_id && s.device_id !== "00000000-0000-0000-0000-000000000000" ? deviceName(s.device_id) : "";
  switch (s.kind) {
    case "test": return s.ping_target ?? "";
    case "interface": return [device, s.interface].filter(Boolean).join(" · ");
    case "snippet": return [device, s.template_id ? templateName(s.template_id) : ""].filter(Boolean).join(" · ");
    case "sync": return s.sync_all ? `All realm devices${s.device_ids?.length ? ` (${s.device_ids.length})` : ""}` : device;
    case "stop": return s.handle ?? "";
    case "manual": return s.instructions ?? "";
    default: return device;
  }
}

/** A file name for the export: the realm's name, safe on any filesystem. */
export function docxName(campaign: string, suffix = ""): string {
  const base = campaign.replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "-") || "campaign";
  return `${base}${suffix ? "-" + suffix : ""}.docx`;
}
